import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/* Utils */
import { isServerSide } from '@utils/environment';

/* Types */
import { Children } from 'types/base.types';

const createRootElement = (id: string) => {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  return rootContainer;
};

const addRootElement = (rootElem: Element) => {
  document.body.insertBefore(rootElem, document.body.lastElementChild!.nextElementSibling);
};

export const usePortal = (id: string) => {
  const [rootElem] = useState<HTMLDivElement | null>(() => (isServerSide() ? null : document.createElement('div')));

  if (isServerSide() || !rootElem) return null;

  useEffect(() => {
    const existingParent = document.querySelector(`#${id}`);
    const parentElem = existingParent || createRootElement(id);

    if (!existingParent) addRootElement(parentElem);

    parentElem.prepend(rootElem);

    return () => {
      rootElem.remove();
      if (parentElem.childNodes.length === 0) parentElem.remove();
    };
  }, [id, rootElem]);

  return rootElem;
};

export const Portal = ({ id, children }: { id: string; children: Children }) => {
  if (isServerSide()) return null;

  const target = usePortal(id);
  if (!target) return null;

  return createPortal(children, target);
};

/*
  usePortal: A hook that handles creating React portals (https://reactjs.org/docs/portals.html)
    - I exported the hook and the component for extra flexibility, but you really only need the component <Portal/>

  Component Usage:
  ---------------

  import { Portal } from 'src/hooks/useCreatePortal';
  ...
  ...
  <Portal id={QUESTIONS_FLOW_CONTAINER_ID}>
    <MyCoolComponent />
  </Portal>

  Hook Usage:
  -----------
  import { usePortal } from 'src/hooks/useCreatePortal';
  import { createPortal } from 'react-dom';
  ...
  const MyCustomPortal = ({children}) => {
    const target = usePortal('#my-cool-id');
    // do some stuff?
    return createPortal(children, target);
  }
  ...
  ...
  <MyCustomPortal>
    <MyCoolComponent/>
  </MyCustomPortal>


  Code taken from: https://www.jayfreestone.com/writing/react-portals-with-hooks/
*/
